import React, { useEffect, useState } from "react";
import { axios } from "../../lib/constants/api";
import { useParams } from "react-router-dom";
import "./gallery-oc.style.scss";
import { Loader } from "../../lib/ui/components";
import Compressor from "compressorjs";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const compressAndConvertToWebP = (file, quality = 0.7) => {
  return new Promise((resolve, reject) => {
    new Compressor(file, {
      quality: quality, // Compression quality
      mimeType: "image/webp", // Convert image to WebP format
      success(result) {
        // Convert the result (which is a File) to a Blob
        result.arrayBuffer().then((buffer) => {
          const blob = new Blob([buffer], { type: "image/webp" });
          resolve(blob); // Return the Blob
        });
      },
      error(err) {
        reject(err); // If there's an error during compression
      },
    });
  });
};
export const GalleryOcComponent: React.FC = () => {
  const apiUrls = {
    upload: "/upload-media-oc/media",
    updateMainImage: (productId) =>
      `/upload-media-oc/product-images/${productId}?mainImage=true`,
    updateImages: (productId) => `/upload-media-oc/product-images/${productId}`,
    fetchImages: (productId) => `/upload-media-oc/product-images/${productId}`,
    fetchProductInfo: (productId) => `/products/panel/${productId}`,
    deleteProductImage: (productId) =>
      `/upload-media-oc/product-images/${productId}?override=true`,
  };

  const { productId } = useParams<{ productId: string }>();
  const [productData, setProductData] = useState(null);
  const [productImages, setProductImages] = useState([]);
  const [isLoadingVisible, setIsLoadingVisible] = useState(true);
  const [uploadingImages, setUploadingImages] = useState([]);
  const [uploadingImagesPreview, setUploadingImagesPreview] = useState([]);
  const [uploadingIndex, setUploadingIndex] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [deletingIndexes, setDeletingIndexes] = useState([]);

  const isQueueEmpty = uploadingImages.length === 0;

  useEffect(() => {
    let isMounted = true;
    
    const fetchImages = async () => {
      try {
        const response = await axios.get(apiUrls.fetchImages(productId));
        if (isMounted) {
          setProductImages(response.data.images);
          setIsLoadingVisible(false);
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error fetching images:', error);
          setIsLoadingVisible(false);
        }
      }
    };

    if (productId) {
      fetchImages();
    }
    
    return () => {
      isMounted = false;
    };
  }, [productId]);

  useEffect(() => {
    let isMounted = true;
    
    const fetchData = async () => {
      try {
        const response = await axios.get(apiUrls.fetchProductInfo(productId));
        if (isMounted) {
          setProductData(response.data);
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error fetching product data:', error);
        }
      }
    };

    if (productId) {
      fetchData();
    }
    
    return () => {
      isMounted = false;
    };
  }, [productId]);

  useEffect(() => {
    // Add the class when the component mounts
    document.body.classList.add("gallery");
    
    // Cleanup function to remove the class when component unmounts
    return () => {
      document.body.classList.remove("gallery");
    };
  }, []);

  const handleUploadImages = async () => {
    setIsUploading(true);
    const uploadedImages = [];

    try {
      // Create FormData with all files at once
      const formData = new FormData();
      uploadingImages.forEach((file, index) => {
        formData.append(`files`, file);
      });

      // Upload all images in a single request
      const response = await axios.post(apiUrls.upload, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const responseData = response.data;
      
      // Handle response - it should be an array of uploaded images
      if (Array.isArray(responseData)) {
        responseData.forEach((imageData) => {
          uploadedImages.push({
            id: imageData.id,
            src: imageData.source_url
          });
        });
      } else {
        // Fallback for single image response
        uploadedImages.push({
          id: responseData.id,
          src: responseData.source_url
        });
      }

      const payload = uploadedImages.map((image) => ({ 
        id: image.id || image.src // fallback to src if id doesn't exist
      }));

      await axios.put(apiUrls.updateImages(productId), payload);

      const fetchResponse = await axios.get(apiUrls.fetchImages(productId));
      setProductImages(fetchResponse.data.images);
      setUploadingImages([]);
      setUploadingImagesPreview([]);
    } catch (error) {
      console.error("Error uploading images:", error);
      // If bulk upload fails, fall back to individual uploads
      await handleIndividualUploads();
    } finally {
      setIsUploading(false);
    }
  };

  const handleIndividualUploads = async () => {
    let currentUploadingIndex = -1;
    const uploadedImages = [];

    for (const uploadingImage of uploadingImages) {
      currentUploadingIndex++;
      setUploadingIndex(currentUploadingIndex);

      // const file = await compressAndConvertToWebP(uploadingImage, 0.6); // Pass the desired quality (0.6 = 60%)

      const formData = new FormData();
      // formData.append("file", file, uploadingImage.name);
      formData.append("file", uploadingImage);

      try {
        const response = await axios.post(apiUrls.upload, formData);
        const responseData = await response.data;
        // Store both id and src for the uploaded image
        uploadedImages.push({
          id: responseData.id,
          src: responseData.source_url
        });
      } catch (error) {
        console.error("Error uploading image:", error);
        return; // Stop uploading if one fails
      }
    }

    try {
      const payload = uploadedImages.map((image) => ({ 
        id: image.id || image.src // fallback to src if id doesn't exist
      }));

      await axios.put(apiUrls.updateImages(productId), payload);

      const fetchResponse = await axios.get(apiUrls.fetchImages(productId));
      setProductImages(fetchResponse.data.images);
      setUploadingImages([]);
      setUploadingImagesPreview([]);
    } catch (error) {
      console.error("Error updating product:", error);
    }
  };

  const handleRemovePendingImage = (index) => {
    const newUploadingImages = uploadingImages.filter((_, i) => i !== index);
    const newUploadingImagesPreview = uploadingImagesPreview.filter(
      (_, i) => i !== index
    );
    setUploadingImages(newUploadingImages);
    setUploadingImagesPreview(newUploadingImagesPreview);
  };

  const handleRemoveUploadedImage = async (image, index) => {
    const newProductImages = productImages.filter((_, i) => i !== index);

    const clonedDeletingIndexes = [...deletingIndexes];
    setDeletingIndexes([...clonedDeletingIndexes, index]);

    try {
      const payload = newProductImages.map((image) => ({ 
        id: image.id || image.src // fallback to src if id doesn't exist
      }));

      await axios.put(apiUrls.deleteProductImage(productId), payload);
      setProductImages(newProductImages);

      const newDeletingIndexes = clonedDeletingIndexes.filter(
        (_, i) => i !== index
      );
      setDeletingIndexes(newDeletingIndexes);
    } catch (error) {
      console.error("Error updating product:", error);
    }
  };

  const handleOtherImagesChange = async (event) => {
    const files = event.target.files;

    const newUploadingImages = [...uploadingImages];
    const newUploadingImagesPreview = [...uploadingImagesPreview];
    for (const file of files) {
      if (file) {
        newUploadingImages.push(file);
        newUploadingImagesPreview.push(URL.createObjectURL(file));
      }
    }
    setUploadingImages(newUploadingImages);
    setUploadingImagesPreview(newUploadingImagesPreview);
  };

  const shouldShowDeleteButton = (index) =>
    deletingIndexes.length ? deletingIndexes.includes(index) : !isUploading;

  const openProductPage = () => {
    if (!productData) return;
    window.open(productData.permalink, "_self");
  };

  return (
    <>
      <Loader visible={isLoadingVisible} text="در حال بارگیری تصاویر..." />

      <div className="gallery-oc-container">
        <button
          className="back-btn btn"
          disabled={!productData || !productData.permalink}
          onClick={() => openProductPage()}
        >
          بازگشت به صفحه محصول
        </button>
        {productData && (
          <h3 className="main-title">{`تصاویر محصول ${productData.name}`}</h3>
        )}
        <div className="images-container">
          <div className="flex">
            <h3 className="container-title">تصاویر دستگاه</h3>
            <div className="upload">
              <input
                disabled={isUploading}
                onChange={handleOtherImagesChange}
                type="file"
                multiple
                id="other-images-upload"
              />
              <label htmlFor="other-images-upload">
                <span className={`btn ${isUploading && "disabled"}`}>{`${
                  productImages.length > 0 ? "افزودن" : "بارگزاری"
                } تصویر`}</span>
              </label>
            </div>
          </div>
          {productImages.map((image, index) => (
            <div className="image-container">
              <div className="cover-action">
                {shouldShowDeleteButton(index) && (
                  <button
                    className={deletingIndexes.includes(index) ? "texted" : ""}
                    disabled={deletingIndexes.includes(index)}
                    onClick={() => handleRemoveUploadedImage(image, index)}
                  >
                    {deletingIndexes.includes(index) ? "در حال حذف" : `\u00D7`}
                  </button>
                )}
              </div>
              <img className="image-item" src={image.src} alt={image.src} />
            </div>
          ))}
          {uploadingImagesPreview.map((image, index) => (
            <div className="image-container preview">
              <div className="cover">
                {uploadingIndex > index
                  ? "آپلود شده"
                  : uploadingIndex === index
                  ? "در حال آپلود"
                  : "در صف آپلود"}
              </div>
              <div className="cover-action">
                {!isUploading && (
                  <button onClick={() => handleRemovePendingImage(index)}>
                    &times;
                  </button>
                )}
              </div>
              <img className="image-item" src={image} alt="text" />
            </div>
          ))}
          {!isQueueEmpty && (
            <button
              disabled={isUploading}
              onClick={() => handleUploadImages()}
              className="confirm-btn"
            >
              {isUploading ? "در حال بارگزاری..." : "بارگزاری تصاویر"}
            </button>
          )}
        </div>
      </div>
    </>
  );
};
